import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SoundscapeMap from './components/SoundscapeMap';
import { SITE_URL, useSeo } from '../../lib/seo';

type CategoryType = '自然の音' | '暮らしの音' | '祭事の音' | '季節の音' | 'all';

interface SoundscapeCard {
  no: number;
  name: string;
  category: CategoryType;
  status: 'published' | 'planned';
  description?: string;
  youtubeId?: string;
  image?: string;
  lat?: number;
  lng?: number;
  recordingYear?: string;
}

const soundscapes: SoundscapeCard[] = [
  {
    no: 1,
    name: '箕面大滝',
    category: '自然の音',
    status: 'published',
    description: '長雨が続いた後の、水量豊かな大滝の様子',
    youtubeId: 'JxiqUct80D4',
    image: '/images/backgrounds/minoh-thumb.jpg',
    lat: 34.8578,
    lng: 135.4783,
    recordingYear: '2026年 6月 20日',
  },
  { no: 2, name: '', category: '自然の音', status: 'planned' },
  { no: 3, name: '', category: '自然の音', status: 'planned' },
  { no: 4, name: '', category: '暮らしの音', status: 'planned' },
  { no: 5, name: '', category: '暮らしの音', status: 'planned' },
  { no: 6, name: '', category: '祭事の音', status: 'planned' },
  { no: 7, name: '', category: '季節の音', status: 'planned' },
  { no: 8, name: '', category: '季節の音', status: 'planned' },
];

const categoryConfig: { key: CategoryType | 'all'; label: string; icon: string }[] = [
  { key: 'all', label: 'すべて', icon: 'ri-global-line' },
  { key: '自然の音', label: '自然の音', icon: 'ri-leaf-line' },
  { key: '暮らしの音', label: '暮らしの音', icon: 'ri-home-heart-line' },
  { key: '祭事の音', label: '祭事の音', icon: 'ri-fire-line' },
  { key: '季節の音', label: '季節の音', icon: 'ri-sun-line' },
];

function HeroSection() {
  const [showPlayer, setShowPlayer] = useState(false);

  return (
    <section className="relative min-h-[520px] flex items-center justify-center overflow-hidden">
      <img
        src="/images/backgrounds/minoh-waterfall.jpg"
        alt="箕面大滝"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/20">
          <i className="ri-sound-module-line"></i>
          <span>Soundscape Archive</span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: '"Noto Serif JP", serif' }}>
          箕面音風景百選
        </h1>
        <p className="text-lg md:text-xl text-white/80 mb-2 tracking-wider">
          Minoh — 100 Soundscapes
        </p>
        <p className="text-sm text-white/60 mb-8 max-w-md mx-auto leading-relaxed">
          箕面の「今」を音で切り取り、未来へ残すアーカイブプロジェクト
        </p>

        {!showPlayer ? (
          <button
            onClick={() => setShowPlayer(true)}
            className="inline-flex items-center gap-3 bg-white/90 hover:bg-white text-gray-800 px-8 py-4 rounded-full font-bold text-lg transition-all cursor-pointer whitespace-nowrap hover:scale-105"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-full">
              <i className="ri-play-fill text-xl"></i>
            </div>
            箕面大滝の音を聴く
          </button>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="relative rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
              <iframe
                src="https://www.youtube.com/embed/JxiqUct80D4?autoplay=1"
                title="箕面大滝の音"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              ></iframe>
            </div>
            <button
              onClick={() => setShowPlayer(false)}
              className="mt-4 text-white/70 hover:text-white text-sm cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-up-s-line mr-1"></i>プレイヤーを閉じる
            </button>
          </div>
        )}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <i className="ri-arrow-down-line text-white/60 text-2xl animate-bounce"></i>
      </div>
    </section>
  );
}

function PhilosophySection() {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-xl mb-6">
                <i className="ri-mic-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: '"Noto Serif JP", serif' }}>
                なぜ箕面の音を残すのか
              </h3>
              <p className="text-gray-600 leading-relaxed text-base">
                音は時代とともに変わっていく。今ある音も、いつか聴けなくなるかもしれない。今の音を録り、後世に伝えていく。
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-xl mb-6">
                <i className="ri-earth-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: '"Noto Serif JP", serif' }}>
                地域との関わり
              </h3>
              <p className="text-gray-600 leading-relaxed text-base">
                まず音で箕面の自然や観光地を感じてもらう。そこから海外の旅行者をこの場所へ引き込むインバウンドのきっかけに。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategorySection({ activeCategory, onCategoryChange }: { activeCategory: CategoryType | 'all'; onCategoryChange: (cat: CategoryType | 'all') => void }) {
  const publishedCards = soundscapes.filter((s) => s.status === 'published');

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: '"Noto Serif JP", serif' }}>カテゴリ</h2>
            <div className="w-16 h-1 bg-teal-600 mx-auto"></div>
          </div>

          <div className="flex justify-center mb-4">
            <div className="inline-flex bg-white rounded-full p-1 flex-wrap gap-1 shadow-sm border border-gray-100">
              {categoryConfig.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => onCategoryChange(cat.key)}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap text-sm ${
                    activeCategory === cat.key
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-gray-200 mt-10">
            <SoundscapeMap
              points={publishedCards
                .filter((c): c is typeof c & { lat: number; lng: number } => c.lat !== undefined && c.lng !== undefined)
                .map((c) => ({ no: c.no, name: c.name, description: c.description, lat: c.lat, lng: c.lng }))}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3 justify-center">
            {publishedCards.map((card) => (
              <div key={card.no} className="flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm text-gray-600 border border-gray-200">
                <div className="w-3 h-3 rounded-full bg-teal-600"></div>
                <span className="font-medium">No.{String(card.no).padStart(3, '0')}</span>
                <span>{card.name}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm text-gray-400 border border-dashed border-gray-300">
              <div className="w-3 h-3 rounded-full bg-gray-300"></div>
              <span>and more...</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SoundscapeCards({ activeCategory }: { activeCategory: CategoryType | 'all' }) {
  const filteredCards = activeCategory === 'all'
    ? soundscapes
    : soundscapes.filter((s) => s.category === activeCategory);

  const publishedCount = soundscapes.filter((s) => s.status === 'published').length;
  const totalCount = 100;

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
              <h2 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Noto Serif JP", serif' }}>音風景一覧</h2>
              <div className="w-16 h-1 bg-teal-600 mt-3"></div>
            </div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 text-sm font-semibold px-5 py-2.5 rounded-full border border-teal-200">
              <i className="ri-headphone-line"></i>
              <span>{publishedCount} / {totalCount} 公開中</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCards.map((card) => (
              card.status === 'published' ? (
                <div key={card.no} id={`soundscape-${card.no}`} className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow group scroll-mt-24">
                  <div className="relative w-full h-52 bg-gray-100 overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      No.{String(card.no).padStart(3, '0')}
                    </div>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full border border-teal-200">
                      <i className="ri-check-line mr-1"></i>公開済み
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-teal-50 text-teal-700 text-xs px-2.5 py-1 rounded-full font-medium">
                        {card.category}
                      </span>
                      {card.recordingYear && (
                        <span className="text-gray-400 text-xs">{card.recordingYear}録音</span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{card.name}</h3>
                    {card.description && (
                      <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.description}</p>
                    )}
                    {card.youtubeId && (
                      <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${card.youtubeId}`}
                          title={card.name}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        ></iframe>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div key={card.no} className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center min-h-[320px] hover:border-teal-300 hover:bg-teal-50/30 transition-all group cursor-default">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-teal-100 transition-colors mb-4">
                    <i className="ri-add-line text-3xl text-gray-400 group-hover:text-teal-600 transition-colors"></i>
                  </div>
                  <p className="text-gray-400 text-sm font-medium mb-1">No.{String(card.no).padStart(3, '0')}</p>
                  <p className="text-gray-400 text-xs">追加予定</p>
                  <p className="text-gray-300 text-xs mt-2">{card.category}</p>
                </div>
              )
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-400 text-sm">
              <i className="ri-information-line mr-1"></i>
              これから増えていくアーカイブです。新しい音風景は順次追加されます。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SubmissionSection() {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-2xl mx-auto mb-6">
            <i className="ri-message-3-line text-3xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: '"Noto Serif JP", serif' }}>
            あなたの「残したい箕面の音」を教えてください
          </h2>
          <div className="w-16 h-1 bg-teal-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-2xl mx-auto">
            箕面にゆかりのある方、地域の音を大切に思う方からの情報提供をお待ちしています。<br />
            投稿フォームや学校・自治会との連携も歓迎いたします。
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/#contact')}
          >
            <i className="ri-send-plane-line mr-2"></i>
            投稿する
          </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedLinksSection() {
  const navigate = useNavigate();

  const links = [
    {
      icon: 'ri-film-line',
      title: '制作実績',
      description: '勝尾寺 語り などの制作実績を見る',
      action: () => navigate('/katsuoji-works'),
      label: '制作実績を見る',
    },
    {
      icon: 'ri-shopping-bag-3-line',
      title: '音源提供（Pond5）について',
      description: 'Pond5での音源ライセンス販売について',
      action: () => window.open('https://www.pond5.com/', '_blank'),
      label: 'Pond5を見る',
    },
    {
      icon: 'ri-mail-line',
      title: 'お問い合わせ',
      description: '音に関するご相談・ご依頼はこちら',
      action: () => navigate('/#contact'),
      label: 'お問い合わせ',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: '"Noto Serif JP", serif' }}>関連リンク</h2>
            <div className="w-16 h-1 bg-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {links.map((link, index) => (
              <button
                key={index}
                onClick={link.action}
                className="bg-gray-50 rounded-xl p-8 text-left border border-gray-100 hover:border-teal-200 hover:bg-teal-50 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5 group-hover:bg-teal-600 transition-colors border border-gray-100">
                  <i className={`${link.icon} text-xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{link.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{link.description}</p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                  {link.label}
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MinohSoundscapesPage() {
  const [activeCategory, setActiveCategory] = useState<CategoryType | 'all'>('all');

  useSeo({
    title: '箕面音風景百選 | 合同会社WakuWaku',
    description:
      '箕面の「今」を音で切り取り、未来へ残すアーカイブプロジェクト。箕面大滝をはじめとする箕面の音風景を収録・公開しています。自然の音、暮らしの音、祭事の音、季節の音。',
    path: '/minoh-soundscapes/',
    keywords: '箕面音風景百選,箕面大滝,自然音,環境音,箕面市,地域サウンドアーカイブ,録音',
  });

  useEffect(() => {
    const siteUrl = SITE_URL;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/minoh-soundscapes/#webpage`,
      url: `${siteUrl}/minoh-soundscapes/`,
      name: '箕面音風景百選 | 合同会社WakuWaku',
      description: '箕面の「今」を音で切り取り、未来へ残すアーカイブプロジェクト。箕面大滝をはじめとする箕面の音風景を収録・公開しています。自然の音、暮らしの音、祭事の音、季節の音。',
      isPartOf: { '@id': `${siteUrl}/#website` },
      inLanguage: 'ja',
      dateModified: '2026-06-28',
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-minoh';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-minoh');
      if (el) document.head.removeChild(el);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-grow">
        <HeroSection />
        <PhilosophySection />
        <CategorySection activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
        <SoundscapeCards activeCategory={activeCategory} />
        <SubmissionSection />
        <RelatedLinksSection />
      </main>

      <Footer />
    </div>
  );
}